import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { AccessShareableLinkResponse, shareableLinksService } from "../services/shareableLinksService";
import { trackingService } from "../services/trackingService";
import { TrackingEvents } from "../services/trackingEvents";
import ShareBasketPage from "../pages/ShareBasketPage";
import SharePortfolioPage from "../pages/SharePortfolioPage";

const PicksRouter: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [resolvedResponse, setResolvedResponse] = useState<AccessShareableLinkResponse | null>(null);
  const [linkType, setLinkType] = useState<"BASKET" | "PORTFOLIO" | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      determineLinkType(token);
    }
  }, [token]);

  const determineLinkType = async (shareToken: string) => {
    try {
      setLoading(true);
      setError(null);
      setLinkType(null);
      setResolvedResponse(null);

      const response = await shareableLinksService.accessShareableLink(shareToken);
      setResolvedResponse(response);
      setLinkType(response.type);
      // NOTE: in production, /picks/:token is intercepted by the load
      // balancer (see comment in shareableLinksService.getShareableUrl)
      // and served by plutus-backend as an SSR page that iframes a share
      // page back into this React app. The backend now always resolves
      // that iframe to /product-share/:token; the legacy /share/:token
      // iframe target was retired along with its route. So this
      // PicksRouter path runs ONLY when the LB rewrite is absent (local
      // dev, isolated preview envs). Do NOT send the raw token: it is the
      // unauthenticated bearer used to fetch the resource.
      trackingService.track(TrackingEvents.SHARE_LINK_VISIT, {
        resource_type: response.type,
        share_route: "picks",
      });
    } catch (err) {
      console.error("Failed to determine link type:", err);
      setError("Failed to load picks link");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading picks...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">{error}</p>
          <p className="text-gray-500 mt-2">The picks link may be invalid or expired.</p>
        </div>
      </div>
    );
  }

  if (linkType === "BASKET") {
    return <ShareBasketPage initialResponse={resolvedResponse} />;
  } else if (linkType === "PORTFOLIO") {
    return <SharePortfolioPage initialResponse={resolvedResponse} />;
  }

  return null;
};

export default PicksRouter;
